// Local-currency restatement helpers.
//
// The canonical local-equivalent restatement lives in `./fxRate.ts`; this
// module retains only project-currency lookups and the voucher-capture path
// (`restateViaCapturedRates`) which uses the FX rates the voucher itself
// stored at payment time.

export interface LocalCurrencyBasis {
	amount: number;
	amountLocal: number | null;
	currencyLocal: string | null;
}

/**
 * The project's local currency code (the `LOCAL` project currency), or null
 * when the project has no local currency configured. This is the currency in
 * which every reporting figure is restated for display across V2.
 */
export const getProjectLocalCurrency = (
	project?: { currencies?: { currency: string; type: string }[] } | null
): string | null => project?.currencies?.find((c) => c.type === 'LOCAL')?.currency ?? null;

/**
 * The project's reporting currency code (the `REPORTING` project currency), or
 * null when none is configured.
 */
export const getProjectReportingCurrency = (
	project?: { currencies?: { currency: string; type: string }[] } | null
): string | null => project?.currencies?.find((c) => c.type === 'REPORTING')?.currency ?? null;

/** A single FX rate captured on a voucher at payment time. */
export interface CapturedRate {
	currency: string;
	exchangeRate: number;
	dateExchangeRate?: string;
}

/**
 * Restate a voucher/payment amount into the project's reporting and local
 * currencies using the FX rates captured on the voucher at payment time.
 *
 * Each captured row means "1 row.currency = row.exchangeRate reportingCurrency"
 * — the convention the prepare-payment dialog uses, where the rate base is the
 * project reporting currency. A leg whose currency matches the amount's own
 * currency returns the amount unchanged; a leg that cannot be resolved from the
 * captured rates returns null so callers render nothing rather than a
 * fabricated figure.
 *
 * Vouchers created before PR-3862 (merged 2026-06-19) stored the raw operator
 * entry rather than the canonical reporting-per-source value, so a 100,000 ETB
 * payment on a USD-reporting project would multiply out to $16.7 million when
 * displayed. Callers can pass `localPerReportingHint` (a ground-truth
 * local-per-reporting rate, e.g. the contract's implied budget rate) and the
 * helper will override the captured rate for the local leg, restating directly
 * from the hint instead of trusting potentially-inverted captured data.
 */
export const restateViaCapturedRates = ({
	amount,
	currency,
	reportingCurrency,
	localCurrency,
	rates,
	localPerReportingHint,
}: {
	amount: number;
	currency: string;
	reportingCurrency: string | null;
	localCurrency: string | null;
	rates?: CapturedRate[] | null;
	localPerReportingHint?: number | null;
}): { reporting: number | null; local: number | null } => {
	const rateFor = (ccy: string): number | null => {
		const row = rates?.find((r) => r.currency === ccy && r.exchangeRate > 0);
		return row ? row.exchangeRate : null;
	};

	// Reporting equivalent: 1 voucherCurrency = rate reportingCurrency.
	let reporting: number | null;
	if (!reportingCurrency || currency === reportingCurrency) {
		reporting = amount;
	} else if (
		localCurrency &&
		currency === localCurrency &&
		localPerReportingHint != null &&
		localPerReportingHint > 0
	) {
		// Local-denominated payment with a ground-truth budget rate: use the
		// hint instead of any captured rate so older vouchers that stored an
		// inverted rate don't blow up the reporting figure.
		reporting = amount / localPerReportingHint;
	} else {
		const r = rateFor(currency);
		reporting = r == null ? null : amount * r;
	}

	// Local equivalent.
	let local: number | null;
	if (!localCurrency) {
		local = null;
	} else if (currency === localCurrency) {
		local = amount;
	} else if (reportingCurrency && localCurrency === reportingCurrency) {
		local = reporting;
	} else if (reporting != null) {
		if (localPerReportingHint != null && localPerReportingHint > 0) {
			// Prefer the hint over the captured rate for the same reason as the
			// reporting leg above.
			local = reporting * localPerReportingHint;
		} else {
			// 1 localCurrency = rateLocal reportingCurrency → local = reporting / rateLocal.
			const rateLocal = rateFor(localCurrency);
			local = rateLocal == null ? null : reporting / rateLocal;
		}
	} else {
		local = null;
	}

	return { reporting, local };
};
